import { useEffect, useState } from "react";
import client from "@/api/client";
import { getFrameIOAccounts, type FrameIOAccount } from "@/api/users";

interface User {
  id: string;
  email: string;
  name: string;
  role: "ADMIN" | "QC" | "CREATIVE";
  is_active: boolean;
  notification_groups: string[];
  frameio_account_ids: string[];
}

export function UserManagementView() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [newUser, setNewUser] = useState<{ email: string; name: string; role: User["role"] }>({
    email: "",
    name: "",
    role: "QC",
  });
  const [frameioAccounts, setFrameioAccounts] = useState<FrameIOAccount[]>([]);
  const [loadingFrameIOAccounts, setLoadingFrameIOAccounts] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await client.get<User[]>("/users");
      setUsers(response.data);
      setError(null);
    } catch (err) {
      setError("Failed to load users");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = async (user: User) => {
    setEditingUser(user);
    setShowEditModal(true);

    // Load Frame.io accounts when opening edit modal
    setLoadingFrameIOAccounts(true);
    try {
      const accounts = await getFrameIOAccounts();
      setFrameioAccounts(accounts);
    } catch (err) {
      console.error("Failed to load Frame.io accounts:", err);
      // Don't block the modal from opening if Frame.io accounts fail to load
      setFrameioAccounts([]);
    } finally {
      setLoadingFrameIOAccounts(false);
    }
  };

  const updateUser = async () => {
    if (!editingUser) return;

    try {
      await client.patch(`/users/${editingUser.id}`, {
        role: editingUser.role,
        is_active: editingUser.is_active,
        notification_groups: editingUser.notification_groups || [],
        frameio_account_ids: editingUser.frameio_account_ids || [],
      });
      await fetchUsers();
      setShowEditModal(false);
      setEditingUser(null);
    } catch (err) {
      console.error("Failed to update user:", err);
      alert("Failed to update user");
    }
  };

  const deleteUser = async (userId: string, userEmail: string) => {
    if (!confirm(`Are you sure you want to delete user ${userEmail}?`)) return;

    try {
      await client.delete(`/users/${userId}`);
      await fetchUsers();
    } catch (err) {
      console.error("Failed to delete user:", err);
      alert("Failed to delete user");
    }
  };

  const createUser = async () => {
    if (!newUser.email || !newUser.name) {
      alert("Email and name are required");
      return;
    }

    try {
      await client.post("/users", newUser);
      await fetchUsers();
      setShowAddModal(false);
      setNewUser({ email: "", name: "", role: "QC" });
    } catch (err: unknown) {
      console.error("Failed to create user:", err);
      const errorMsg =
        (err as { response?: { data?: { detail?: string } } }).response?.data?.detail ||
        "Failed to create user";
      alert(errorMsg);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-gray-600">Loading users...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-red-600">{error}</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900">User Management</h1>
        <button
          onClick={() => setShowAddModal(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          Add User
        </button>
      </div>

      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Email
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Role
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Notifications
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Frame.io Accounts
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {users.map((user) => (
              <tr key={user.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{user.email}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{user.name}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="text-sm text-gray-900 capitalize">{user.role}</span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`px-3 py-1.5 inline-flex items-center text-sm font-medium rounded-md ${
                      user.is_active
                        ? "bg-green-100 text-green-800"
                        : "bg-gray-100 text-gray-800"
                    }`}
                  >
                    {user.is_active ? "Active" : "Inactive"}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex gap-1">
                    {user.notification_groups?.map((group) => (
                      <span
                        key={group}
                        className="px-2 py-1 text-xs font-medium rounded-md bg-blue-100 text-blue-800"
                      >
                        {group}
                      </span>
                    ))}
                    {(!user.notification_groups || user.notification_groups.length === 0) && (
                      <span className="text-sm text-gray-400">None</span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  {user.role === "ADMIN" ? (
                    <span className="text-gray-600 italic">All accounts</span>
                  ) : user.frameio_account_ids && user.frameio_account_ids.length > 0 ? (
                    <span className="text-gray-900">
                      {user.frameio_account_ids.length}{" "}
                      {user.frameio_account_ids.length === 1 ? "account" : "accounts"}
                    </span>
                  ) : (
                    <span className="text-gray-400">None</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => openEditModal(user)}
                      className="px-3 py-1.5 bg-blue-50 text-blue-700 text-sm font-medium rounded-md border border-blue-300 hover:bg-blue-100 hover:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-300 focus:ring-offset-2 transition-colors"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => deleteUser(user.id, user.email)}
                      className="px-3 py-1.5 bg-red-50 text-red-700 text-sm font-medium rounded-md border border-red-300 hover:bg-red-100 hover:border-red-400 focus:outline-none focus:ring-2 focus:ring-red-300 focus:ring-offset-2 transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {users.length === 0 && (
          <div className="text-center py-8 text-gray-500">No users found</div>
        )}
      </div>

      {/* Add User Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Add New User</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="user@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={newUser.name}
                  onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <select
                  value={newUser.role}
                  onChange={(e) =>
                    setNewUser({
                      ...newUser,
                      role: e.target.value as "ADMIN" | "QC" | "CREATIVE",
                    })
                  }
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="ADMIN">Admin</option>
                  <option value="QC">QC</option>
                  <option value="CREATIVE">Creative</option>
                </select>
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setNewUser({ email: "", name: "", role: "QC" });
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Cancel
              </button>
              <button
                onClick={createUser}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Create User
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && editingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Edit User</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="text"
                  value={editingUser.email}
                  disabled
                  className="w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-100 text-gray-500 cursor-not-allowed"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={editingUser.name}
                  disabled
                  className="w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-100 text-gray-500 cursor-not-allowed"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <select
                  value={editingUser.role}
                  onChange={(e) =>
                    setEditingUser({
                      ...editingUser,
                      role: e.target.value as "ADMIN" | "QC" | "CREATIVE",
                    })
                  }
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="ADMIN">Admin</option>
                  <option value="QC">QC</option>
                  <option value="CREATIVE">Creative</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <div className="flex items-center space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="status"
                      checked={editingUser.is_active === true}
                      onChange={() => setEditingUser({ ...editingUser, is_active: true })}
                      className="mr-2 focus:ring-blue-500"
                    />
                    <span className="text-sm">Active</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="status"
                      checked={editingUser.is_active === false}
                      onChange={() => setEditingUser({ ...editingUser, is_active: false })}
                      className="mr-2 focus:ring-blue-500"
                    />
                    <span className="text-sm">Inactive</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Notifications
                </label>
                <div className="flex items-center space-x-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={editingUser.notification_groups?.includes("CREATIVE") ?? false}
                      onChange={(e) => {
                        const groups = e.target.checked
                          ? [...(editingUser.notification_groups || []), "CREATIVE"]
                          : (editingUser.notification_groups || []).filter((g) => g !== "CREATIVE");
                        setEditingUser({ ...editingUser, notification_groups: groups });
                      }}
                      className="mr-2 focus:ring-blue-500"
                    />
                    <span className="text-sm">Creative Notifications</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={editingUser.notification_groups?.includes("QC") ?? false}
                      onChange={(e) => {
                        const groups = e.target.checked
                          ? [...(editingUser.notification_groups || []), "QC"]
                          : (editingUser.notification_groups || []).filter((g) => g !== "QC");
                        setEditingUser({ ...editingUser, notification_groups: groups });
                      }}
                      className="mr-2 focus:ring-blue-500"
                    />
                    <span className="text-sm">QC Notifications</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Frame.io Accounts
                </label>
                {editingUser.role === "ADMIN" ? (
                  <p className="text-sm text-gray-600 italic">
                    Admin users have access to all Frame.io accounts automatically.
                  </p>
                ) : loadingFrameIOAccounts ? (
                  <div className="text-sm text-gray-500">Loading Frame.io accounts...</div>
                ) : frameioAccounts.length === 0 ? (
                  <div className="text-sm text-gray-500">
                    No Frame.io accounts available. Please check your Frame.io API connection.
                  </div>
                ) : (
                  <div className="border border-gray-200 rounded-md p-3 max-h-48 overflow-y-auto space-y-2">
                    {frameioAccounts.map((account) => {
                      // Display account name if available, otherwise show a readable shortened ID
                      const displayName = account.name
                        ? account.name
                        : account.id
                        ? `Frame.io Account (${account.id.slice(0, 8)}...${account.id.slice(-4)})`
                        : "Unnamed Account";

                      return (
                        <label key={account.id} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={editingUser.frameio_account_ids?.includes(account.id) ?? false}
                            onChange={(e) => {
                              const accountIds = e.target.checked
                                ? [...(editingUser.frameio_account_ids || []), account.id]
                                : (editingUser.frameio_account_ids || []).filter(
                                    (id) => id !== account.id
                                  );
                              setEditingUser({ ...editingUser, frameio_account_ids: accountIds });
                            }}
                            className="mr-2 focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-900" title={account.id}>
                            {displayName}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingUser(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Cancel
              </button>
              <button
                onClick={updateUser}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
